import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ExportPanel = ({ onExport }) => {
  const exportOptions = [
    {
      format: 'pdf',
      title: 'PDF Report',
      description: 'Comprehensive business intelligence report with charts and insights',
      icon: 'FileText',
      color: 'primary',
      size: '~2.5 MB'
    },
    {
      format: 'excel',
      title: 'Excel Data Export',
      description: 'Raw data and metrics in spreadsheet format for custom analysis',
      icon: 'FileSpreadsheet',
      color: 'success',
      size: '~850 KB'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      primary: { bg: 'bg-primary/10', icon: 'var(--color-primary)', button: 'primary' },
      success: { bg: 'bg-success/10', icon: 'var(--color-success)', button: 'success' }
    };
    return colors?.[color] || colors?.primary;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6 elevation-sm">
      <div className="flex items-start gap-2 md:gap-3 mb-4 md:mb-6">
        <div className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
          <Icon name="Download" size={18} className="md:w-5 md:h-5" color="var(--color-primary)" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base md:text-lg font-semibold text-foreground font-heading break-words">
            Export Reports
          </h3>
          <p className="text-xs md:text-sm text-muted-foreground font-caption break-words">
            Download comprehensive reports for stakeholder communication
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 mb-4 md:mb-6">
        {exportOptions?.map((option, index) => {
          const colorClasses = getColorClasses(option?.color);
          return (
            <div
              key={index}
              className="border border-border rounded-lg p-3 md:p-4 hover:elevation-sm transition-smooth"
            >
              <div className="flex items-start gap-2 md:gap-3 mb-3 md:mb-4">
                <div className={`w-10 h-10 md:w-12 md:h-12 rounded-lg ${colorClasses?.bg} flex items-center justify-center flex-shrink-0`}>
                  <Icon name={option?.icon} size={20} className="md:w-6 md:h-6" color={colorClasses?.icon} />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm md:text-base font-semibold text-foreground font-heading mb-1 break-words">
                    {option?.title}
                  </h4>
                  <p className="text-xs md:text-sm text-muted-foreground font-caption mb-2 break-words">
                    {option?.description}
                  </p>
                  <span className="text-xs text-muted-foreground font-caption">
                    Estimated size: {option?.size}
                  </span>
                </div>
              </div>
              <Button
                variant={colorClasses?.button}
                size="default"
                fullWidth
                iconName="Download"
                iconPosition="left"
                onClick={() => onExport(option?.format)}
              >
                Download {option?.format?.toUpperCase()}
              </Button>
            </div>
          );
        })}
      </div>

      {/* Premium Features Notice */}
      <div className="bg-warning/10 border border-warning/20 rounded-lg p-3 md:p-4">
        <div className="flex items-start gap-2 md:gap-3">
          <Icon name="Lock" size={14} className="md:w-4 md:h-4 flex-shrink-0" color="var(--color-warning)" />
          <div className="flex-1 min-w-0">
            <p className="text-xs md:text-sm font-medium text-foreground font-body mb-1 break-words">
              Premium Feature
            </p>
            <p className="text-xs text-muted-foreground font-caption break-words">
              Report exports are available with Growth or Scale plans. Upgrade now to unlock unlimited downloads and scheduled report delivery.
            </p>
          </div>
        </div>
      </div>

      {/* Scheduled Reports (Coming Soon) */}
      <div className="mt-4 md:mt-6 pt-4 md:pt-6 border-t border-border">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 md:gap-0">
          <div className="flex-1 min-w-0">
            <h4 className="text-xs md:text-sm font-semibold text-foreground font-body mb-1 break-words">
              Scheduled Report Delivery
            </h4>
            <p className="text-xs text-muted-foreground font-caption break-words">
              Automatically receive reports via email - Coming Soon
            </p>
          </div>
          <div className="px-2.5 md:px-3 py-1 bg-muted rounded-md flex-shrink-0">
            <span className="text-xs font-medium text-muted-foreground font-caption">
              Coming Soon
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportPanel;